import { AccessFunction } from 'data/accessFunction/accessFunction.types';
import { CollectionFunction, HI2Interface } from 'data/collectionFunction/collectionFunction.types';
import { Location } from 'data/location/location.types';
import { NodeData } from 'data/nodes/nodes.types';
import cloneDeep from 'lodash.clonedeep';
import { mockGenericAccessFunction } from '__test__/mockAccessFunctions';
import {
  mockGenericConnectionFunction,
  mockNetworkTopologyCfInterfacesByType,
  mockHi2INterfaces,
  mockHi3Interfaces,
} from '__test__/mockCollectionFunctions';
import { mockLocations } from '__test__/mockLocations';
import { NetworkTopologyStateEnum, XcipioNodeMOckDataEntry } from './network-topology.types';

type LocationMockType = { stateName: string, cities?: string[], equallyDistribute?: boolean };

const poiStates = [
  NetworkTopologyStateEnum.ACTIVE,
  NetworkTopologyStateEnum.INACTIVE,
  NetworkTopologyStateEnum.FAILED,
  NetworkTopologyStateEnum.BLOCKED,
  NetworkTopologyStateEnum.IDLE
];

function randomNumber(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min) + min);
}

const setAfInterfaceStatus = (poiInterfaces: any[]) => {
  if (!poiInterfaces?.length) {
    return [];
  }

  return poiInterfaces.map((poiInterface) => {
    return {
      ...poiInterface,
      options: poiInterface.options.map((option: { key: string, value: string }) => {
        if (option.key !== 'reqState' && option.key !== 'state') {
          return option;
        }

        return {
          ...option,
          value: poiStates[randomNumber(0, 5)]
        }
      })
    }
  })
}

const buildAccessFunction = (type: string, index: number) => {
  const idName = `AF-${type}-${index}`;
  return {
    ...mockGenericAccessFunction,
    id: btoa(idName),
    name: idName,
    tag: type,
    type,
    provisioningInterfaces: setAfInterfaceStatus(mockGenericAccessFunction.provisioningInterfaces as any),
    contentInterfaces: setAfInterfaceStatus(mockGenericAccessFunction.contentInterfaces as any),
    dataInterfaces: setAfInterfaceStatus(mockGenericAccessFunction.dataInterfaces as any),
  }
}

const setCfInterfaceStatus = (poiInterfaces: HI2Interface[]) => {
  if (!poiInterfaces) {
    return [];
  }

  return poiInterfaces.map((poiInterface) => {
    return {
      ...poiInterface,
      options: (poiInterface.options || []).map((option: { key: string, value: string }) => {
        if (option.key !== 'reqState' && option.key !== 'state') {
          return option;
        }
        return {
          ...option,
          value: poiStates[randomNumber(0, 5)]
        }
      })
    }
  })
}

const buildCollectionFunction = (type: string, index: number) => {
  const idName = `CF-${type}-${index}`;
  const cfType = type as 'GPRS' | 'ETSIIP' | 'TIA1066' | 'T1.678' | 'CALEA' | 'TIA1072' | 'T1IAS';

  const hasHi3Interfaces = randomNumber(0, 2) === 0;

  const h2Interfaces = mockNetworkTopologyCfInterfacesByType[cfType]?.hi2Interfaces ? mockHi2INterfaces : [];
  const h3Interfaces = mockNetworkTopologyCfInterfacesByType[cfType]?.hi3Interfaces ? mockHi3Interfaces : [];

  return {
    ...mockGenericConnectionFunction,
    id: btoa(idName),
    name: idName,
    tag: type,
    type,
    hi2Interfaces: setCfInterfaceStatus(h2Interfaces),
    hi3Interfaces: hasHi3Interfaces ? setCfInterfaceStatus(h3Interfaces) : [],
  }
}

const generateListByTypes = (afTypesQuantityMap: { [tag: string]: number }, poiType: 'AF' | 'CF',) => {
  return (type: string) => {
    return Array(afTypesQuantityMap[type])
      .fill(type)
      .map((type, index) => {
        if (poiType === 'CF') {
          return buildCollectionFunction(type, index);
        }
        return buildAccessFunction(type, index);
      });
  };
}

const generatePoiListBasedOnTypeQuantity = (
  afTypesQuantityMap: { [tag: string]: number },
  poiType: 'AF' | 'CF',
) => {
  return Object.keys(afTypesQuantityMap)
    .map(generateListByTypes(afTypesQuantityMap, poiType))
    .reduce((total, actual) => [...total, ...actual], []);
}

const setLocation = (stateName: string, city: string) => (af: AccessFunction) => {
  const clonedAf = cloneDeep(af);
  return { ...clonedAf, city, stateName };
};

function getChunk(baseList: unknown[], listToSetChunks: unknown[]) {
  if (baseList?.length >= listToSetChunks?.length) {
    return Math.floor(baseList.length / listToSetChunks.length);
  }
  return baseList.length;
}

function sliceIntoChunks<T>(list: T[], chunkSize: number): T[][] {
  const clonedList = cloneDeep(list);
  const chunkList: T[][] = [];
  for (let i = 0; i < clonedList.length; i += chunkSize) {
    const chunk = clonedList.slice(i, i + chunkSize);
    chunkList.push(chunk);
  }

  return chunkList;
}

const getCities = (
  location: LocationMockType,
  countryLocations: Location[],
) => {
  const { cities } = location;
  if (cities?.length) {
      return cities;
  }

  const stateCities = countryLocations.find((state) => state.stateName === location.stateName)?.cities || [];

  return stateCities.map((city) => city.city);
}

const generateRandomIndex = (maxLimit: number):number => {
  const rand: number = Math.random() * maxLimit;
  return Math.floor(rand);
};

function distributeAfsAcrossLocation(
  afs: AccessFunction[],
  locationsToSetAfs: LocationMockType[],
  useRadomLocations?: boolean
):AccessFunction[] {
  const chunkSize = getChunk(afs, cloneDeep(locationsToSetAfs));
  const afStatesChunks = sliceIntoChunks(afs, chunkSize);
  const clonedAfStatesChunks = cloneDeep(afStatesChunks);
  return cloneDeep(locationsToSetAfs)
    .map((loc, index) => {
        if (!clonedAfStatesChunks[index]) {
          return [];
        }

        const cities = getCities(loc, mockLocations);
        const stateAfs = cloneDeep(clonedAfStatesChunks[index]);
        const afCitiesChunks = sliceIntoChunks<AccessFunction>(stateAfs, cities.length);
        if (!loc.cities?.length) {
          return stateAfs
            .map((af) => {
                const { stateName } = loc;
                const city = cities[generateRandomIndex(cities.length)];
                return setLocation(stateName, city)(af);
            })
            .filter((af) => af != null);
        }

        return afCitiesChunks
            .map((afChunk) => {
                const { stateName } = loc;
                const city = cities[generateRandomIndex(cities.length)];
                if (!city) {
                    return [];
                }
                return afChunk.map(setLocation(stateName, city));
            })
            .filter((chunk) => chunk != null)
            .reduce((total, actual) => [...total, ...actual], []);
    })
    .filter((chunk) => chunk != null)
    .reduce((total, actual) => [...total, ...actual], []);
}

function distributeCcpagAfsAcrossLocation(
  afs: AccessFunction[],
  locationsToSetAfs: LocationMockType[],
  useSecondCity?: boolean,
):AccessFunction[] {
  return cloneDeep(afs)
    .map((af, index) => {
      const { cities, stateName } = locationsToSetAfs[index];
      const city = useSecondCity ?
        ((cities as any)[1] || (cities as any)[0]) :
        (cities as any)[0];

      const afWithLocation = setLocation(stateName, city)(af);
      return afWithLocation;
    });
}

export function generateAccessFunctions(
  afTypesQuantityMap: { [tag: string]: number },
  locationsMap?: LocationMockType[]
): AccessFunction[] {
  const listOfGenericAccessFunctions = generatePoiListBasedOnTypeQuantity(afTypesQuantityMap, 'AF');
  if (!locationsMap) {
    return listOfGenericAccessFunctions;
  }
  return distributeAfsAcrossLocation(listOfGenericAccessFunctions, locationsMap);
}

export function generateCcpagAccessFunctions(
  afTypesQuantityMap: { [tag: string]: number },
  locationsMap?: LocationMockType[],
  useSecondCity?: boolean,
): AccessFunction[] {
  const listOfGenericAccessFunctions = generatePoiListBasedOnTypeQuantity(afTypesQuantityMap, 'AF');
  if (!locationsMap) {
    return listOfGenericAccessFunctions;
  }
  return distributeCcpagAfsAcrossLocation(listOfGenericAccessFunctions, locationsMap, useSecondCity);
}

export const generateCollectionFunctions = (
  afTypesQuantityMap: { [tag: string]: number },
  locationsMap?: LocationMockType[]
): any[] => {
  const listOfGenericCollectionFunctions = generatePoiListBasedOnTypeQuantity(afTypesQuantityMap, 'CF');
  if (!locationsMap) {
    return listOfGenericCollectionFunctions;
  }

  return distributeAfsAcrossLocation(listOfGenericCollectionFunctions, locationsMap, true);
}

const buildXcipioNodeInArray = (
  total:number,
  nodeType: string,
  locations?: { latitude?: number, longitude?: number, country?: string, city?: string, stateCode?: string }[],
  topology = 'STANDALONE',
): NodeData[] => {
  return Array(total)
    .fill(nodeType)
    .map((nodeType, index) => {
      const nodeName = `${nodeType}-node-${index}`;
      return {
        id: btoa(nodeName),
        nodeName,
        nodeType,
        topology,
        ipAddress: '10.0.129.216',
        ipAddress6: '::',
        destNodeName: '',
        latitude: locations?.[index].latitude || 40.56002,
        longitude: locations?.[index].longitude || -74.71612,
        country: locations?.[index].country || '',
        stateCode: locations?.[index].stateCode || '',
        city: locations?.[index].city || '',
      }
    })
}

export const generateXcipioNodes = (dataInput?: {
  amdf: XcipioNodeMOckDataEntry,
  ccpag: XcipioNodeMOckDataEntry,
  mdf2: XcipioNodeMOckDataEntry,
  mdf3: XcipioNodeMOckDataEntry,
}): NodeData[] => {
  if (!dataInput) {
    return [
      {
        id: 'Y2NwYWctYi0xMjktMjE2',
        nodeName: 'XCP-node-1',
        nodeType: 'XCP',
        topology: 'STANDALONE',
        ipAddress: '10.0.129.216',
        ipAddress6: '::',
        destNodeName: ''
      }
    ];
  }
  return [
    ...buildXcipioNodeInArray(dataInput.amdf.total, 'ADMF', dataInput.ccpag.locations),
    ...buildXcipioNodeInArray(dataInput.ccpag.total, 'CCPAG', dataInput.ccpag.locations),
    ...buildXcipioNodeInArray(dataInput.mdf2.total, 'MDF2', dataInput.mdf2.locations),
    ...buildXcipioNodeInArray(dataInput.mdf3.total, 'MDF3', dataInput.mdf3.locations),
  ];
}

const xcipioNodesByType = (nodeType: string) => (node: NodeData) => node.nodeType === nodeType;

export const generatePoiXcipioNodeMapMock = (
  afMocks: AccessFunction[],
  cfMocks: CollectionFunction[],
  xcipioNodesMocks: NodeData[],
  locations?: Location[] | null,
): any[] => {
  const mdf2 = xcipioNodesMocks.filter(xcipioNodesByType('MDF2'));
  const mdf3 = xcipioNodesMocks.filter(xcipioNodesByType('MDF3'));
  const ccpag = xcipioNodesMocks.filter(xcipioNodesByType('CCPAG'));
  const nonErkUpfAfs = afMocks.filter((af) => af.tag !== 'ERK_UPF');
  const erkUpfAfs = afMocks.filter((af) => af.tag === 'ERK_UPF');
  const afPerNode = Math.floor(nonErkUpfAfs.length/2 + 0.5);

  if (!locations?.length) {
    const afChunks = sliceIntoChunks(nonErkUpfAfs, afPerNode);
    const mdf2AFsPOIChunk = generatePoiNodeMap(afChunks[0] || [], mdf2);
    const mdf3AFsPOIChunk = generatePoiNodeMap(afChunks[1] || [], mdf3);
    const mdf2CFsPOIChunk = generatePoiNodeMap(cfMocks || [], mdf2);
    const ccpagAFsPOIChunk = generatePoiNodeMap(erkUpfAfs || [], ccpag);
    return [
      ...mdf2AFsPOIChunk,
      ...mdf3AFsPOIChunk,
      ...ccpagAFsPOIChunk,
      ...mdf2CFsPOIChunk,
    ];
  }

  const mdf2Mdf3AFsPOIChunk = generatePoiNodeMapByLocation(nonErkUpfAfs, [...mdf2, ...mdf3], locations);
  const mdf2CFsPOIChunk = generatePoiNodeMapByLocation(cfMocks, mdf2, locations);
  const ccpagAFsPOIChunk = generateCcpagPoiNodeMapByLocation(erkUpfAfs, ccpag, locations);

  return [
    ...mdf2Mdf3AFsPOIChunk,
    ...mdf2CFsPOIChunk,
    ...ccpagAFsPOIChunk,
  ];
}

const generatePoiNodeMap = (afCfData: AccessFunction[], nodes: NodeData[]) => {
  const poiData = cloneDeep(afCfData).sort((a: any, b: any) => a.stateName - b.stateName);
  const xcipioNodes = cloneDeep(nodes).sort((a: any, b: any) => a.stateCode - b.stateCode);

  const xcipioNodeList = poiData.length > xcipioNodes.length ?
  xcipioNodes : xcipioNodes.slice(0, poiData.length);
  const xcipioNodeAFs = Math.floor(poiData.length/xcipioNodeList.length + 0.5);
  const xcipioNodeAFsChunk = sliceIntoChunks(cloneDeep(poiData), xcipioNodeAFs);

  return xcipioNodeAFsChunk.map((chunk, index) => {
    if (!xcipioNodes?.[index]) {
      return [];
    }
    return chunk.map((af) => ({
      id: af.id,
      poiId: af.id,
      liType: af.type,
      name: af.name,
      destNodeName: xcipioNodeList[index].nodeName,
    }))
  })
  .reduce((total, actual) => [...total, ...actual], []);
}

const getLocationsLatLongMaps = (locations: Location[]) => {
  return locations
    .reduce((t, a) => {
      return {
        ...t,
        [a.stateName]: (a.cities || []).reduce((ct, c) => ({ ...ct, [c.city]: { latitude: c.latitude, longitude: c.longitude } }), {}),
      };
    }, {});
}

const generatePoiNodeMapByLocation = (
  afCfData: AccessFunction[],
  nodes: NodeData[],
  locations: Location[]
) => {

  const locationsMap: any = getLocationsLatLongMaps(locations);
  const connectionsMap = setConnectionsToNode(afCfData, nodes, locationsMap);

  return Object
    .keys(connectionsMap)
    .map((key) => connectionsMap[key])
    .reduce((t, a) => [...t, ...a], []);
}

const generateCcpagPoiNodeMapByLocation = (
  afCfData: AccessFunction[],
  nodes: NodeData[],
  locations: Location[]
) => {

  const locationsMap: any = getLocationsLatLongMaps(locations);
  const connectionsMap = setConnectionsToNode(afCfData, nodes, locationsMap, true);

  return Object
    .keys(connectionsMap)
    .map((key) => connectionsMap[key])
    .reduce((t, a) => [...t, ...a], []);
}

function getPointsDistance(
  pointA: { latitude: number; longitude: number },
  pointB: { latitude: number; longitude: number }
) {
  const x = (pointB.latitude - pointA.latitude)**2;
  const y = (pointB.longitude - pointA.longitude)**2;

  return (x + y)**0.5;
}

function connectPoiToXcipioNode(
  poi: any,
  nodes: any[],
  poiPerNode: number,
  xcipioNodeConnectionsMap: any,
  index: number,
  isCcpag?: boolean
): void {
  if (index > nodes.length) {
    return;
  }

  let node = isCcpag ? nodes.find((node) => node.stateCode === poi.stateName && node.nodeType === 'CCPAG') : nodes[index];

  if (isCcpag && node) {
    return xcipioNodeConnectionsMap[node.nodeName].push({
      id: poi.id,
      poiId: poi.id,
      liType: poi.type,
      name: poi.name,
      destNodeName: node.nodeName,
      nodeType: node.nodeType,
    });
  }

  node = nodes[index];

  if (!node) {
    return;
  }

  if (xcipioNodeConnectionsMap[node.nodeName].length < poiPerNode) {
    return xcipioNodeConnectionsMap[node.nodeName].push({
      id: poi.id,
      poiId: poi.id,
      liType: poi.type,
      name: poi.name,
      destNodeName: node.nodeName,
      nodeType: node.nodeType,
    });
  }

  connectPoiToXcipioNode(
    poi,
    nodes,
    poiPerNode,
    xcipioNodeConnectionsMap,
    index + 1
  );

}

const sortXcipioNodesByAfDistance = (af: any, xcipioNodes: any[], locationsMap: any) => {
  return cloneDeep(xcipioNodes).sort((nodeA, nodeB) => {
    const distanceA = getPointsDistance(
      locationsMap[af.stateName]?.[af.city],
      locationsMap[nodeA.stateCode]?.[nodeA.city]
    );
    const distanceB = getPointsDistance(
      locationsMap[af.stateName]?.[af.city],
      locationsMap[nodeB.stateCode]?.[nodeB.city]
    );
    return distanceA - distanceB;
  })
}

const sortPoisByDistanceToCenter = (pois: any[], locationsMap: any, isNode?: boolean) => {
  const center = {
    latitude: 40.56002,
    longitude: -74.71612,
  }

  return cloneDeep(pois).sort((poiA, poiB) => {
    const distanceA = getPointsDistance(
      center,
      locationsMap[isNode ? poiA.stateCode :poiA.stateName]?.[poiA.city]
    );
    const distanceB = getPointsDistance(
      center,
      locationsMap[isNode ? poiB.stateCode: poiB.stateName]?.[poiB.city]
    );
    return distanceA - distanceB;
  })
}

const getSortedNodes = (afs: any[], nodes: any[], locationsMap: any) => {
  const xcipioNodes = nodes.length >= afs.length ? cloneDeep(nodes) : nodes.slice(0, afs.length);

  return sortPoisByDistanceToCenter(xcipioNodes, locationsMap, true);
}

function setConnectionsToNode(afs: any[], nodes: any[], locationsMap: any, isCcpag?: boolean) {
  const xcipioNodes = getSortedNodes(afs, nodes, locationsMap);
  const sortedAfs = sortPoisByDistanceToCenter(afs, locationsMap);
  const xcipioNodeConnectionsMap = xcipioNodes.reduce((t, a) => ({ ...t, [a.nodeName]: [] }), {});
  const afsPerNode = Math.floor(sortedAfs.length / xcipioNodes.length + 0.5);

  sortedAfs.forEach((af) => {
    connectPoiToXcipioNode(
      af,
      sortXcipioNodesByAfDistance(af, xcipioNodes, locationsMap),
      afsPerNode,
      xcipioNodeConnectionsMap,
      0,
      isCcpag
    );
  });

  return xcipioNodeConnectionsMap;
}

export const generateCcpagMdf3ConnectionsMap = (
  xcipioNodesMocks: NodeData[],
  locations: Location[],
): { [key:string]: string } => {
  const ccpagXcipioNodes = xcipioNodesMocks.filter(xcipioNodesByType('CCPAG'));
  const mdf3XcipioNodes = xcipioNodesMocks.filter(xcipioNodesByType('MDF3'));
  const ccpagsPerMdf3 = Math.floor(ccpagXcipioNodes.length/mdf3XcipioNodes.length + 0.5);
  const xcipioNodeAFsChunk = sliceIntoChunks(ccpagXcipioNodes, ccpagsPerMdf3);

  return xcipioNodeAFsChunk
    .map((ccpagsChunk, index) => {
      if (!mdf3XcipioNodes[index]) {
        return [];
      }
      return ccpagsChunk.map((ccpag) => ({
        [ccpag.id]: mdf3XcipioNodes[index].id,
      }))
    })
    .reduce((total, actual) => [...total, ...actual], [])
    .reduce((total, actual) => ({
      ...total,
      ...actual
    }), {});
}

export const getAFMockLocations = (shouldUseNetworkTopologyDinamicLocations?: boolean) => {
  if (shouldUseNetworkTopologyDinamicLocations) {
    return [
      {
        stateName: 'New York',
        cities: [],
      },
      {
        stateName: 'Alabama',
        cities: []
      },
      {
        stateName: 'Arizona',
        cities: []
      },
      {
        stateName: 'California',
        cities: []
      },
      {
        stateName: 'Colorado',
        cities: []
      },
      {
        stateName: 'District of Columbia',
        cities: []
      },
      {
        stateName: 'Florida',
        cities: [],
      },
      {
        stateName: 'Iowa',
        cities: [],
      },
      {
        stateName: 'Kansas',
        cities: [],
      },

      {
        stateName: 'Texas',
        cities: [],
      },
      {
        stateName: 'Washington',
        cities: [],
      },
    ];
  }

  return [
    {
      stateName: 'Alabama',
      cities: [
        'Montgomery', 'Abbeville', 'Aliceville',
      ]
    },
    {
      stateName: 'Arizona',
      cities: [
        'Phoenix', 'Prescott', 'Safford',
      ]
    },
    {
      stateName: 'California',
      cities: [
        'Sacramento', 'Santa Barbara', 'Bakersfield',
      ]
    },
    {
      stateName: 'Colorado',
      cities: [
        'Denver', 'Alamosa', 'Aurora',
      ]
    },
    {
      stateName: 'Florida',
      cities: [
        'Tallahassee', 'Arcadia', 'Belleview',
      ],
    },
    {
      stateName: 'Iowa',
      cities: [
        'Des Moines', 'Afton', 'Algona',
      ],
    },
    {
      stateName: 'Kansas',
      cities: [
        'Topeka', 'Abbyville', 'Belvue',
      ],
    },
    {
      stateName: 'New York',
      cities: [
        'New York City', 'Albany', 'Utica',
      ],
    },
    {
      stateName: 'Texas',
      cities: [
        'Austin', 'Houston', 'Alvarado',
      ],
    },
    {
      stateName: 'Washington',
      cities: [
        'Seattle', 'Aberdeen', 'Buckley',
      ],
    },
    {
      stateName: 'Illinois',
      cities: [
        'Springfield', 'Abingdon', 'Rushville',
      ],
    },

    {
      stateName: 'Missouri',
      cities: [
        'Jefferson City', 'Laddonia', 'Albany',
      ],
    },
    {
      stateName: 'South Dakota',
      cities: [
        'Pierre', 'Lennox', 'Columbia',
      ],
    },
    {
      stateName: 'Nebraska',
      cities: [
        'Lincoln', 'Nelson', 'Deshler',
      ],
    },
    {
      stateName: 'Wyoming',
      cities: [
        'Cheyenne', 'Kemmerer', 'Riverton',
      ],
    },
  ];
}

export const getCcpagLocations = () => {
  return [
    {
      country: 'US',
      stateCode: 'Alabama',
      stateName: 'Alabama',
      city: 'Huntsville',
      cities: ['Huntsville', 'Birmingham'],
    },
    {
      country: 'US',
      stateCode: 'Arkansas',
      stateName: 'Arkansas',
      city: 'Little Rock',
      cities: ['Little Rock', 'Benton'],
    },
    {
      country: 'US',
      stateCode: 'Arizona',
      stateName: 'Arizona',
      city: 'Phoenix',
      cities: ['Phoenix', 'Buckeye'],
    },
    {
      country: 'US',
      stateCode: 'California',
      stateName: 'California',
      city: 'Los Angeles',
      cities: ['Los Angeles', 'Santa Ana'],
    },
    {
      country: 'US',
      stateCode: 'Colorado',
      stateName: 'Colorado',
      city: 'Denver',
      cities: ['Denver'],
    },
    {
      country: 'US',
      stateCode: 'Connecticut',
      stateName: 'Connecticut',
      city: 'Bridgeport',
      cities: ['Bridgeport'],
    },
    {
      country: 'US',
      stateCode: 'District of Columbia',
      stateName: 'District of Columbia',
      city: 'Washington',
      cities: ['Washington'],
    },
    {
      country: 'US',
      stateCode: 'Delaware',
      stateName: 'Delaware',
      city: 'Delaware City',
      cities: ['Delaware City', 'Dover'],
    },
    {
      country: 'US',
      stateCode: 'Florida',
      stateName: 'Florida',
      city: 'Jacksonville',
      cities: ['Jacksonville', 'Gainesville'],
    },
    {
      country: 'US',
      stateCode: 'Georgia',
      stateName: 'Georgia',
      city: 'Atlanta',
      cities: ['Atlanta', 'Griffin'],
    },
    {
      country: 'US',
      stateCode: 'Iowa',
      stateName: 'Iowa',
      city: 'Des Moines',
      cities: ['Des Moines'],
    },
    {
      country: 'US',
      stateCode: 'Idaho',
      stateName: 'Idaho',
      city: 'Bancroft',
      cities: ['Bancroft', 'Pocatello'],
    },
    {
      country: 'US',
      stateCode: 'Illinois',
      stateName: 'Illinois',
      city: 'Chicago',
      cities: ['Chicago', 'Waukegan'],
    },
    {
      country: 'US',
      stateCode: 'Indiana',
      stateName: 'Indiana',
      city: 'Indianapolis',
      cities: ['Indianapolis', 'Frankfort'],
    },
    {
      country: 'US',
      stateCode: 'Kansas',
      stateName: 'Kansas',
      city: 'Wichita',
      cities: ['Wichita', 'Newton'],
    },
    {
      country: 'US',
      stateCode: 'Kentucky',
      stateName: 'Kentucky',
      city: 'Louisville',
      cities: ['Louisville', 'Springfield'],
    },
    {
      country: 'US',
      stateCode: 'Louisiana',
      stateName: 'Louisiana',
      city: 'New Orleans',
      cities: ['New Orleans', 'Lafayette'],
    },
    {
      country: 'US',
      stateCode: 'Massachusetts',
      stateName: 'Massachusetts',
      city: 'Boston',
      cities: ['Boston', 'Worcester'],
    },
    {
      country: 'US',
      stateCode: 'Maryland',
      stateName: 'Maryland',
      city: 'Baltimore',
      cities: ['Baltimore'],
    },
    {
      country: 'US',
      stateCode: 'Maine',
      stateName: 'Maine',
      city: 'Portland',
      cities: ['Portland', 'Augusta'],
    },
    {
      country: 'US',
      stateCode: 'Michigan',
      stateName: 'Michigan',
      city: 'Lansing',
      cities: ['Lansing'],
    },
    {
      country: 'US',
      stateCode: 'Minnesota',
      stateName: 'Minnesota',
      city: 'Minneapolis',
      cities: ['Minneapolis'],
    },
    {
      country: 'US',
      stateCode: 'Missouri',
      stateName: 'Missouri',
      city: 'Kansas City',
      cities: ['Kansas City'],
    },
    {
      country: 'US',
      stateCode: 'Mississippi',
      stateName: 'Mississippi',
      city: 'Jackson',
      cities: ['Jackson', 'Crystal Springs'],
    },
    {
      country: 'US',
      stateCode: 'Montana',
      stateName: 'Montana',
      city: 'Billings',
      cities: ['Billings'],
    },
    {
      country: 'US',
      stateCode: 'North Carolina',
      stateName: 'North Carolina',
      city: 'Cherryville',
      cities: ['Cherryville', 'Charlotte'],
    },
    {
      country: 'US',
      stateCode: 'North Dakota',
      stateName: 'North Dakota',
      city: 'Fargo',
      cities: ['Fargo'],
    },
    {
      country: 'US',
      stateCode: 'Nebraska',
      stateName: 'Nebraska',
      city: 'Omaha',
      cities: ['Omaha'],
    },
    {
      country: 'US',
      stateCode: 'New Hampshire',
      stateName: 'New Hampshire',
      city: 'Manchester',
      cities: ['Manchester'],
    },
    {
      country: 'US',
      stateCode: 'New Jersey',
      stateName: 'New Jersey',
      city: 'Newark',
      cities: ['Newark'],
    },
    {
      country: 'US',
      stateCode: 'New Mexico',
      stateName: 'New Mexico',
      city: 'Albuquerque',
      cities: ['Albuquerque'],
    },
    {
      country: 'US',
      stateCode: 'Nevada',
      stateName: 'Nevada',
      city: 'Las Vegas',
      cities: ['Las Vegas'],
    },
    {
      country: 'US',
      stateCode: 'New York',
      stateName: 'New York',
      city: 'New York City',
      cities: ['New York City'],
    },
    {
      country: 'US',
      stateCode: 'Ohio',
      stateName: 'Ohio',
      city: 'Columbus',
      cities: ['Columbus'],
    },
    {
      country: 'US',
      stateCode: 'Oklahoma',
      stateName: 'Oklahoma',
      city: 'Oklahoma City',
      cities: ['Oklahoma City'],
    },
    {
      country: 'US',
      stateCode: 'Oregon',
      stateName: 'Oregon',
      city: 'Portland',
      cities: ['Portland'],
    },
    {
      country: 'US',
      stateCode: 'Pennsylvania',
      stateName: 'Pennsylvania',
      city: 'Philadelphia',
      cities: ['Philadelphia'],
    },
    {
      country: 'US',
      stateCode: 'Rhode Island',
      stateName: 'Rhode Island',
      city: 'Providence',
      cities: ['Providence'],
    },
    {
      country: 'US',
      stateCode: 'South Carolina',
      stateName: 'South Carolina',
      city: 'Charleston',
      cities: ['Charleston'],
    },
    {
      country: 'US',
      stateCode: 'South Dakota',
      stateName: 'South Dakota',
      city: 'Sioux Falls',
      cities: ['Sioux Falls'],
    },
    {
      country: 'US',
      stateCode: 'Tennessee',
      stateName: 'Tennessee',
      city: 'Brentwood',
      cities: ['Brentwood'],
    },
    {
      country: 'US',
      stateCode: 'Texas',
      stateName: 'Texas',
      city: 'Houston',
      cities: ['Houston', 'Sealy'],
    },
    {
      country: 'US',
      stateCode: 'Utah',
      stateName: 'Utah',
      city: 'Salt Lake City',
      cities: ['Salt Lake City'],
    },
    {
      country: 'US',
      stateCode: 'Virginia',
      stateName: 'Virginia',
      city: 'Virginia Beach',
      cities: ['Virginia Beach'],
    },
    {
      country: 'US',
      stateCode: 'Vermont',
      stateName: 'Vermont',
      city: 'Burlington',
      cities: ['Burlington'],
    },
    {
      country: 'US',
      stateCode: 'Washington',
      stateName: 'Washington',
      city: 'Seattle',
      cities: ['Seattle'],
    },
    {
      country: 'US',
      stateCode: 'Wisconsin',
      stateName: 'Wisconsin',
      city: 'Milwaukee',
      cities: ['Milwaukee'],
    },
    {
      country: 'US',
      stateCode: 'West Virginia',
      stateName: 'West Virginia',
      city: 'Charleston',
      cities: ['Charleston'],
    },
    {
      country: 'US',
      stateCode: 'Wyoming',
      stateName: 'Wyoming',
      city: 'Cheyenne',
      cities: ['Cheyenne'],
    },
  ];
}
